import { _ } from "~/i18n";
import { getImage } from "./utils";
import getWasmTexturesData, { TWasmTextureNames } from "./textures-data";
import { WASM_TEXTURES } from "./const";
import { getCircleImage, getTextMetrics, getTextureMetaData } from "./textures-utils";
import { IWasmCustomData } from "~/interfaces";
import { CONFIG } from "~/config";
import { TPositionShareImageVariant } from "./store/interfaces";

interface ICustomTexture {
	imageData: ImageData;
	canvas: HTMLCanvasElement;
}

export default class WasmTextures {
	#image: HTMLImageElement;

	#canvas: HTMLCanvasElement;

	#ctx: CanvasRenderingContext2D;

	constructor() {
		this.#image = null;
		this.#canvas = document.createElement("canvas");
		this.#ctx = this.#canvas.getContext("2d", { willReadFrequently: true });
	}

	async load() {
		const image = await getImage(`${WASM_TEXTURES.mainTexture}?version=${CONFIG.version}`);

		this.#image = image;

		if (this.#image) {
			this.#canvas.width = this.#image.naturalWidth;
			this.#canvas.height = this.#image.naturalHeight;
			this.#ctx.drawImage(this.#image, 0, 0);
		} else {
			this.#canvas.width = WASM_TEXTURES.defSize;
			this.#canvas.height = WASM_TEXTURES.defSize;
			this.#ctx.fillStyle = "red";
			this.#ctx.fillRect(0, 0, WASM_TEXTURES.defSize, WASM_TEXTURES.defSize);
		}
	}

	getWasmTexture(name: TWasmTextureNames) {
		const metaData = getTextureMetaData(name);
		// souradnice z textur
		const imageData = metaData.data
			? this.#ctx.getImageData(metaData.data.x, metaData.data.y, metaData.data.width, metaData.data.height)
			: this.#ctx.getImageData(0, 0, WASM_TEXTURES.defSize, WASM_TEXTURES.defSize);

		return {
			width: metaData.width,
			height: metaData.height,
			anchorX: metaData.anchorX,
			anchorY: metaData.anchorY,
			imageData,
		};
	}

	/**
	 * Ziskani markeru pro vlastni body
	 */
	getSmallNumber({
		value,
		isActive = false,
		opacity = 1,
	}: {
		value: number;
		isActive?: boolean;
		opacity?: number;
	}): ICustomTexture {
		const data = getWasmTexturesData(isActive ? "small-active" : "small-inactive");
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		// vykreslime marker
		ctx.globalAlpha = opacity;
		// nemuzu put image data, protoze tam pak nejde nastavit opacity
		ctx.drawImage(this.#image, data.x, data.y, data.width, data.height, 0, 0, data.width, data.height);
		// text - 13px 1x, 26px 2x
		ctx.font = "26px Arial";
		ctx.fillStyle = "black";

		const drawText = value.toString();
		let offsetLeft = 0;
		const offsetTop = 40;

		if (drawText.length === 1) {
			offsetLeft = 24;
		} else if (drawText.length === 2) {
			offsetLeft = 17;
		} else if (drawText.length === 3) {
			offsetLeft = 10;
		}

		ctx.fillText(drawText, offsetLeft, offsetTop);

		return {
			imageData: ctx.getImageData(0, 0, data.width, data.height),
			canvas: newCanvas,
		};
	}

	/**
	 * Ziskani textove ikonky pro routu start/cil
	 */
	getGetRouteStartEnd({
		isEnd = false,
		isActive = false,
		opacity = 1,
	}: {
	isEnd?: boolean;
	isActive?: boolean;
	opacity?: number;
	}): ICustomTexture {
		const textureName = isActive ? "big-active" : "big-inactive";
		const metaData = getTextureMetaData(textureName);
		const data = getWasmTexturesData(textureName);
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		ctx.globalAlpha = opacity;
		// vykreslime marker
		// nemuzu put image data, protoze tam pak nejde nastavit opacity
		ctx.drawImage(this.#image, data.x, data.y, data.width, data.height, 0, 0, data.width, data.height);
		// text - 13px 1x, 26px 2x
		ctx.font = "22px Arial";
		ctx.fillStyle = "black";

		const value = isEnd
			? _("route.item.end")
			: _("route.item.start");
		const drawText = value.toLocaleUpperCase();
		const measure = ctx.measureText(drawText);
		// musime znovu vynasobit 2x - je to retina
		const offsetLeft = (metaData.anchorX * WASM_TEXTURES.ratio) - (measure.width / 2);
		const offsetTop = 55;

		ctx.fillText(drawText, offsetLeft, offsetTop);

		return {
			imageData: ctx.getImageData(0, 0, data.width, data.height),
			canvas: newCanvas,
		};
	}

	getLocationShareDeviceNumber(order: number, isActive: boolean): ICustomTexture {
		const newCanvas = document.createElement("canvas");
		const data = getWasmTexturesData(isActive ? "position-share-device-active" : "position-share-device-inactive");
		const ctx = newCanvas.getContext("2d");
		const drawText = order.toString();

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		// vykreslime marker
		ctx.putImageData(this.#ctx.getImageData(data.x, data.y, data.width, data.height), 0, 0);
		// text
		ctx.font = `bold ${14 * WASM_TEXTURES.ratio}px Arial`;
		ctx.fillStyle = "#5f7182";

		const metrics = ctx.measureText(drawText);
		const fontWidth = metrics.width;
		const fontHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

		ctx.fillText(drawText, (data.width / 2) - (fontWidth / 2), data.height - (2 * fontHeight) - (1 * WASM_TEXTURES.ratio));

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}

	async getLocationShareImage(url: string, variant: TPositionShareImageVariant): Promise<ICustomTexture> {
		const imageData = await getCircleImage(url, 40 * WASM_TEXTURES.ratio, variant === "gray-active" || variant === "gray-inactive");
		const imageCanvas = imageData.canvas;
		const newCanvas = document.createElement("canvas");
		const data = getWasmTexturesData(variant === "active" || variant === "gray-active" ? "position-share-image-active" : "position-share-image-inactive");
		const ctx = newCanvas.getContext("2d");
		const pos = (data.width - imageCanvas.width) / 2;

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		// kolecko
		ctx.putImageData(this.#ctx.getImageData(data.x, data.y, data.width, data.height), 0, 0);
		// fotka
		ctx.drawImage(imageCanvas, 0, 0, imageCanvas.width, imageCanvas.height, pos, pos - 2, imageCanvas.width, imageCanvas.height);

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}

	windAngle(angle: number) {
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");
		const data = getWasmTexturesData("wind-arrow");
		const halfWidth = data.width / 2;
		const halfHeight = data.height / 2;
		const arrowCanvas = document.createElement("canvas");
		const ctxArrow = arrowCanvas.getContext("2d");

		arrowCanvas.width = data.width;
		arrowCanvas.height = data.height;
		newCanvas.width = data.width;
		newCanvas.height = data.height;
		ctxArrow.drawImage(this.#image, data.x, data.y, data.width, data.height, 0, 0, data.width, data.height);
		ctx.translate(halfWidth, halfHeight);
		ctx.rotate((angle * Math.PI) / 180);
		ctx.drawImage(arrowCanvas, -halfWidth, -halfHeight, data.width, data.height);

		return newCanvas;
	}

	/* eslint-disable-next-line */
	createPubtMidpoint(size: number, drawText: string) {
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");
		const radiusHalf = size / 2;
		const borderWidth = 2 * WASM_TEXTURES.ratio;

		newCanvas.width = size;
		newCanvas.height = size;
		ctx.beginPath();
		ctx.fillStyle = "#e1e4e7";
		ctx.arc(radiusHalf, radiusHalf, radiusHalf, 0, Math.PI * 2);
		ctx.fill();
		ctx.beginPath();
		ctx.fillStyle = "#fff";
		ctx.arc(radiusHalf, radiusHalf, radiusHalf - borderWidth, 0, Math.PI * 2);
		ctx.fill();
		ctx.fillStyle = "#677888";

		const font = `bold ${12 * WASM_TEXTURES.ratio}px arial`;
		const textMeasure = getTextMetrics(ctx, drawText, font);

		ctx.font = font;
		ctx.fillText(drawText, (size - textMeasure.width) / 2, ((size - textMeasure.height) / 2) + textMeasure.height - 4);

		return newCanvas;
	}

	/** Max 5 ikonek na radku */
	createroutePopupPubtSummary(pubtData: IWasmCustomData["routePopupPubtSummary"]): ICustomTexture {
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");
		const padding = 4 * WASM_TEXTURES.ratio;
		const font = `${16 * WASM_TEXTURES.ratio}px arial`;
		let width = 0;
		let height = 0;
		const perLine = 5;
		const iconsLen = pubtData.icons.length;
		const lines = Math.ceil(iconsLen / perLine);
		const iconSize = 24 * WASM_TEXTURES.ratio;
		const iconsWidth = (iconSize * Math.min(perLine, iconsLen)) + ((Math.min(iconsLen, perLine) - 1) * padding);
		const warning = getWasmTexturesData("warning");

		ctx.fillStyle = "#000000";

		const textMeasure = getTextMetrics(ctx, pubtData.text, font);

		width = textMeasure.width;

		if (pubtData.warning) {
			width += padding + warning.width;
		}

		height = textMeasure.height + (iconsLen > 0 ? padding : 0) + ((lines * iconSize) + (lines * padding));

		newCanvas.width = Math.max(width, iconsWidth);
		newCanvas.height = height;

		ctx.font = font;
		ctx.fillText(pubtData.text, 0, textMeasure.height);

		if (pubtData.warning) {
			ctx.drawImage(this.#image, warning.x, warning.y, warning.width, warning.height, textMeasure.width + padding, padding / 2, warning.width, warning.height);
		}

		let iconPosY = textMeasure.height + (padding * 2);

		for (let ind = 0, max = iconsLen; ind < max; ind++) {
			const curPosX = ind % perLine;

			if (ind > 0 && curPosX === 0) {
				iconPosY += padding + iconSize;
			}

			const iconPosX = curPosX > 0
				? (iconSize * curPosX) + (curPosX * padding)
				: 0;

			if (pubtData.icons[ind].icon) {
				const data = getWasmTexturesData(pubtData.icons[ind].icon);

				ctx.drawImage(this.#image, data.x, data.y, data.width, data.height, iconPosX, iconPosY, data.width, data.height);
			} else if (pubtData.icons[ind].midpoint) {
				const midPoint = this.createPubtMidpoint(iconSize, pubtData.icons[ind].midpoint);

				ctx.drawImage(midPoint, iconPosX, iconPosY);
			}
		}

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}

	createroutePopupPubtSection(pubtData: IWasmCustomData["routePopupPubtSection"]): ICustomTexture {
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");

		newCanvas.width = 20;
		newCanvas.height = 20;
		/* eslint-disable-next-line */
		console.log([this, pubtData]);
		ctx.fillStyle = "#f00";
		ctx.fillRect(0, 0, 20, 20);

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}

	createroutePopupClosure(closureData: IWasmCustomData["routePopupClosure"]): ICustomTexture {
		const newCanvas = document.createElement("canvas");
		const ctx = newCanvas.getContext("2d");
		const iconSize = 20 * WASM_TEXTURES.ratio;
		const data = getWasmTexturesData(closureData.icon);
		const padding = 4 * WASM_TEXTURES.ratio;
		const font = `${12 * WASM_TEXTURES.ratio}px arial`;
		// ikonka je 20
		let width = iconSize;
		const textMeasure = getTextMetrics(ctx, closureData.text, font);

		width += padding + textMeasure.width + padding;
		newCanvas.width = width;
		newCanvas.height = iconSize;
		ctx.drawImage(this.#image, data.x, data.y, data.width, data.height, 0, 0, iconSize, iconSize);
		ctx.fillStyle = "#c00";
		ctx.font = font;
		ctx.fillText(closureData.text, iconSize + padding, ((iconSize - textMeasure.height) / 2) + textMeasure.height - 4);

		return {
			imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
			canvas: newCanvas,
		};
	}
}
